import { Link } from "react-router-dom";
import { businessConfig } from "@/config/business.config";
import { services } from "@/data/services";

const social = businessConfig.online.social;
const facebookUrl = (social.facebook as string) !== "TO CONFIRM" ? social.facebook : "#";
const instagramUrl = (social.instagram as string) !== "TO CONFIRM" ? social.instagram : "#";
const googleUrl = businessConfig.online.googleBusinessProfileUrl;

/** Segmented diamond-blade mark used in the footer lockup. */
function BladeMark() {
  return (
    <svg viewBox="0 0 120 66" width="50" height="28" aria-hidden="true" style={{ marginLeft: 32, marginBottom: 1 }}>
      <defs>
        <linearGradient id="fzbl2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#EDEEEF" />
          <stop offset=".5" stopColor="#B7BABD" />
          <stop offset="1" stopColor="#7C8084" />
        </linearGradient>
        <linearGradient id="fzhub2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#606367" />
          <stop offset="1" stopColor="#212327" />
        </linearGradient>
      </defs>
      <path d="M3 63 A57 57 0 0 1 117 63 Z" fill="url(#fzbl2)" />
      <g stroke="#71757A" strokeWidth="3.2" strokeLinecap="round">
        <line x1="97.6" y1="49.3" x2="110.7" y2="44.5" />
        <line x1="90.6" y1="37.3" x2="101.4" y2="28.3" />
        <line x1="80" y1="28.4" x2="87" y2="16.2" />
        <line x1="66.95" y1="23.6" x2="69.4" y2="9.8" />
        <line x1="53" y1="23.6" x2="50.6" y2="9.8" />
        <line x1="40" y1="28.4" x2="33" y2="16.2" />
        <line x1="29.4" y1="37.3" x2="18.6" y2="28.3" />
        <line x1="22.4" y1="49.3" x2="9.3" y2="44.5" />
      </g>
      <g fill="#181715">
        <circle cx="95.2" cy="33.4" r="2.1" />
        <circle cx="68" cy="17.7" r="2.1" />
        <circle cx="37" cy="23.2" r="2.1" />
        <circle cx="16.8" cy="47.3" r="2.1" />
      </g>
      <path d="M22 63 A38 38 0 0 1 98 63 Z" fill="url(#fzhub2)" />
    </svg>
  );
}

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="ftr">
      <div className="container">
        <div className="ftr__top">
          <div className="ftr__brand">
            <span className="ftr__lockup">
              <BladeMark />
              <span className="ftr__wordmark">
                <span className="ftr__wordmark-f">F</span>RAZZETTO
              </span>
            </span>
            <p className="ftr__tagline">
              Family-run concrete cutting, core drilling, sawing and removal across{" "}
              {businessConfig.serviceRegion.primaryLabel}. Residential, commercial and construction —{" "}
              {businessConfig.people.principal.yearsOfIndustryExperience} years in the trade.
            </p>
            <div className="ftr__phones">
              <a href={businessConfig.contact.phoneHref} className="ftr__phone">
                <span aria-hidden="true">☎</span> {businessConfig.contact.phoneDisplay}
              </a>
            </div>
            <div className="ftr__social">
              <a href={facebookUrl} aria-label="Facebook" target="_blank" rel="noopener" className="ftr__social-btn">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                  <path d="M13.5 21v-8h2.6l.4-3h-3V8.2c0-.9.3-1.5 1.6-1.5H16.6V4c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8V10H8v3h2.7v8h2.8z" />
                </svg>
              </a>
              <a href={googleUrl} aria-label="Google reviews" target="_blank" rel="noopener" className="ftr__social-btn ftr__social-btn--g">
                G
              </a>
              <a href={instagramUrl} aria-label="Instagram" target="_blank" rel="noopener" className="ftr__social-btn">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
                  <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.2" cy="6.8" r="1.1" fill="#fff" stroke="none" />
                </svg>
              </a>
            </div>
          </div>

          <div className="ftr__col">
            <h3 className="ftr__h">Services</h3>
            <ul className="ftr__list">
              {services.slice(0, 6).map((service) => (
                <li key={service.slug}>
                  <Link to={`/services/${service.slug}`}>{service.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="ftr__col">
            <h3 className="ftr__h">Company</h3>
            <ul className="ftr__list">
              <li><Link to="/about">About us</Link></li>
              <li><Link to="/service-areas">Service areas</Link></li>
              <li><Link to="/capabilities">Capabilities</Link></li>
              <li><Link to="/safety-compliance">Safety &amp; compliance</Link></li>
              <li><Link to="/faqs">FAQs</Link></li>
              <li><Link to="/#reviews">Reviews</Link></li>
              <li><Link to="/contact">Get a quote</Link></li>
              <li className="ftr__static">{businessConfig.hours.display}</li>
            </ul>
          </div>
        </div>

        <div className="ftr__bottom">
          <span>
            © {year} {businessConfig.identity.tradingName} · ABN {businessConfig.identity.abn}
          </span>
          <span className="ftr__legal">
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <span>Public liability insured · Melbourne, VIC</span>
          </span>
        </div>
      </div>

      <style>{`
        .ftr {
          background: var(--graphite-deep);
          color: var(--footer-muted);
          border-top: 1px solid rgba(255,255,255,0.08);
          font-family: var(--font-body);
          padding: clamp(44px, 5vw, 64px) 0 26px;
        }
        .ftr__top { display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 32px; }
        .ftr__lockup { display: inline-flex; flex-direction: column; align-items: flex-start; line-height: 1; }
        .ftr__wordmark {
          font-family: var(--font-display); font-style: italic; font-weight: 900;
          font-size: 24px; letter-spacing: -0.02em; text-transform: uppercase; color: #fff;
        }
        .ftr__wordmark-f { color: var(--orange); }
        .ftr__tagline { font-size: 0.96rem; line-height: 1.6; margin-top: 16px; max-width: 40ch; }

        .ftr__phones { display: flex; flex-direction: column; gap: 6px; margin-top: 18px; }
        .ftr__phone {
          display: inline-flex; align-items: center; gap: 10px; color: #fff;
          font-family: var(--font-display); font-weight: 900; font-size: 20px; text-decoration: none;
        }
        .ftr__phone span[aria-hidden] { color: var(--orange); }
        .ftr__phone-who { font-family: var(--font-mono); font-size: 11px; font-weight: 400; color: var(--footer-muted); }

        .ftr__social { display: flex; gap: 10px; margin-top: 18px; }
        .ftr__social-btn {
          width: 38px; height: 38px; border-radius: 50%; background: var(--graphite-panel);
          border: 1px solid rgba(255,255,255,0.14); display: flex; align-items: center; justify-content: center;
          color: #fff; font-family: var(--font-display); font-weight: 900; font-size: 16px; text-decoration: none;
          transition: background var(--duration-fast) var(--ease-standard);
        }
        .ftr__social-btn:hover { background: var(--orange); }

        .ftr__h { color: #fff; font-family: var(--font-display); font-weight: 800; font-size: 13px; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 14px; }
        .ftr__list { display: flex; flex-direction: column; gap: 9px; font-size: 0.94rem; }
        .ftr__list a { color: #b0aaa1; text-decoration: none; }
        .ftr__list a:hover { color: var(--orange); }
        .ftr__static { color: var(--footer-muted); }

        .ftr__bottom {
          border-top: 1px solid rgba(255,255,255,0.1); margin-top: 36px; padding-top: 20px;
          display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap;
          font-family: var(--font-mono); font-size: 11.5px; color: #726c63;
        }
        .ftr__legal { display: flex; gap: 18px; flex-wrap: wrap; }
        .ftr__legal a { color: #726c63; text-decoration: none; }
        .ftr__legal a:hover { color: var(--orange); }

        @media (max-width: 760px) { .ftr__top { grid-template-columns: 1fr 1fr; } .ftr__brand { grid-column: 1 / -1; } }
        @media (max-width: 480px) { .ftr__top { grid-template-columns: 1fr; } }
        /* Clear the fixed mobile action bar so the footer's bottom row isn't hidden behind it. */
        @media (max-width: 899px) { .ftr { padding-bottom: calc(26px + 64px + env(safe-area-inset-bottom)); } }
      `}</style>
    </footer>
  );
}
